import { FC, useCallback } from "react";
import Router, { useRouter } from "next/router";
import { useAtomValue } from "jotai";

import { useUpdateEmployeeFeatureFlagsMutation } from "@src/features/app/top-nav/SetEmployeeFeatureFlags.generated";
import { track } from "@src/features/app/utils/track";
import {
  useFeatureFlag,
  useFeatureFlagsLoaded,
} from "@src/features/app/hooks/useFeatureFlag";
import { libraryItemAtom } from "@src/features/libraryItem/libraryItemAtom";
import { LIBRARY_ITEM_OVERVIEW_TAB } from "@src/features/libraryItem/contexts/defaultMainTab";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";
import { useToast } from "@src/lib/hooks/useToast";
import { Button } from "@src/lib/ui/Button";
import Pill from "@src/lib/ui/Pill";
import Text from "@src/lib/ui/Text";
import { launchConfetti } from "@src/lib/utils/confetti";
import { Ff, GraphqlOperations, LibraryItemType } from "@src/types.generated";

const DETAIL_PAGE = "/library/library-item/[id]";

const LibraryItemOverviewOptIn: FC = () => {
  const { pathname } = useRouter();
  const libraryItemType = useAtomValue(libraryItemAtom)?.type;
  const flagsLoaded = useFeatureFlagsLoaded();
  const optInEnabled = useFeatureFlag(Ff.LibraryItemOverviewTabOptIn);
  const enabled = useFeatureFlag(Ff.LibraryItemOverviewTab);
  const [dismissed, setDismissed] = useBooleanLocalStorage(
    "dismissed_library_item_overview_opt_in",
    false,
  );
  const { addErrorToast } = useToast();
  const showOptInError = useCallback(
    () =>
      addErrorToast({
        message: "Couldn't turn on the new content pages",
        callsite: "library_item_overview_opt_in",
      }),
    [addErrorToast],
  );
  const [updateEmployeeFeatureFlags, { loading }] =
    useUpdateEmployeeFeatureFlagsMutation({
      refetchQueries: [GraphqlOperations.Query.FeatureFlags],
      // The previous page has no Overview tab: switching the URL before the
      // flags land would bounce the user to its first tab (the builder).
      awaitRefetchQueries: true,
    });

  const handleTry = useCallback(() => {
    updateEmployeeFeatureFlags({
      variables: {
        input: {
          featureFlags: [
            {
              name: Ff.LibraryItemOverviewTab,
              enabled: { value: true },
            },
          ],
        },
      },
      onCompleted: (data) => {
        if (!data.updateEmployeeFeatureFlags.success) {
          showOptInError();
          return;
        }
        // Land on the new Overview rather than wherever the previous page
        // was (usually Build). SCORM courses have no Overview and keep their
        // Manage sub-tab. The live router, not the click-time one: the user
        // may have moved on while the flags refreshed.
        if (
          Router.pathname === DETAIL_PAGE &&
          libraryItemType !== LibraryItemType.ScormCourse
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { manageTab: _manageTab, ...query } = Router.query;
          Router.replace(
            {
              pathname: Router.pathname,
              query: { ...query, mainTab: LIBRARY_ITEM_OVERVIEW_TAB },
            },
            undefined,
            { shallow: true },
          );
        }
        launchConfetti();
        track("feature-opt-in", {
          feature: "Library Item Overview",
          enabled: true,
        });
      },
      onError: showOptInError,
    });
  }, [updateEmployeeFeatureFlags, showOptInError, libraryItemType]);

  if (
    !optInEnabled ||
    pathname !== DETAIL_PAGE ||
    !flagsLoaded ||
    enabled ||
    dismissed
  ) {
    return null;
  }

  return (
    <div
      data-testid="library-item-overview-opt-in"
      className="flex w-[220px] flex-col rounded-2xl border border-border-primary bg-bg-primary p-3 shadow-default"
    >
      <div className="flex self-stretch pb-1">
        <Pill title="New" color="purple" />
      </div>
      <Text variant="compact-title" className="pb-1 text-text-primary">
        New content pages
      </Text>
      <Text
        variant="fine-print-title"
        multiline
        className="text-text-secondary"
      >
        Overview, assignments, and reporting — redesigned.
      </Text>
      <Button
        size="sm"
        className="mt-3 self-stretch"
        onClick={handleTry}
        loading={loading}
      >
        Try it
      </Button>
      <button
        type="button"
        className="mt-2 cursor-pointer self-end text-text-tertiary hover:text-text-secondary"
        onClick={() => setDismissed(true)}
      >
        <Text variant="fine-print-title">Dismiss</Text>
      </button>
    </div>
  );
};

export default LibraryItemOverviewOptIn;
